/**
 * Excel Upload Modal Component
 *
 * Provides a drag-and-drop interface for uploading Excel/CSV files
 * for bulk import into master data tables.
 */

import React, { useState } from 'react';
import { Modal, Button, Upload, message } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import './BulkImport.css';

const { Dragger } = Upload;

const ExcelUploadModal = ({
  visible,
  onClose,
  onFileSelect,
  tableId,
  tableName
}) => {
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);

  const uploadProps = {
    name: 'file',
    multiple: false,
    accept: '.csv,.xlsx,.xls',
    maxCount: 1,
    fileList: fileList,
    beforeUpload: (file) => {
      // Validate file type
      const validTypes = [
        'text/csv',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];

      const isValidType = validTypes.includes(file.type) ||
        file.name.endsWith('.csv') ||
        file.name.endsWith('.xlsx') ||
        file.name.endsWith('.xls');

      if (!isValidType) {
        message.error('Invalid file type. Please upload CSV or Excel files only.');
        return Upload.LIST_IGNORE;
      }

      // Validate file size (50MB)
      const maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        message.error('File size exceeds 50MB limit.');
        return Upload.LIST_IGNORE;
      }

      setFileList([file]);
      return false; // Prevent auto upload
    },
    onRemove: () => {
      setFileList([]);
    }
  };

  const handleUpload = () => {
    if (fileList.length === 0) {
      message.error('Please select a file to upload.');
      return;
    }

    setUploading(true);
    onFileSelect(fileList[0]);
  };

  const handleClose = () => {
    setFileList([]);
    setUploading(false);
    onClose();
  };

  return (
    <Modal
      title={`Bulk Import - ${tableName}`}
      open={visible}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>,
        <Button
          key="upload"
          type="primary"
          loading={uploading}
          onClick={handleUpload}
          disabled={fileList.length === 0}
        >
          Next
        </Button>
      ]}
      width={600}
      className="bulk-import-upload"
    >
      <div style={{ marginBottom: 16 }}>
        <p>Upload a CSV or Excel file to import data into <strong>{tableName}</strong>.</p>
        <p style={{ color: '#8c8c8c', fontSize: '12px' }}>
          Maximum file size: 50MB. Supported formats: .csv, .xlsx, .xls
        </p>
      </div>

      <Dragger {...uploadProps}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined style={{ fontSize: 48, color: '#1890ff' }} />
        </p>
        <p className="ant-upload-text">
          Click or drag file to this area to upload
        </p>
        <p className="ant-upload-hint">
          Support for CSV, XLSX, and XLS files (max 50MB)
        </p>
      </Dragger>
    </Modal>
  );
};

export default ExcelUploadModal;
